import type { FinancialCarrier } from '@/const/Financial'
import type { FinancialFilePreview, FinancialWorksheetPreview } from '@/types/Components/FinancialFilePreview'
import type { CellValue, Sheet } from 'read-excel-file/browser'
import { unzipSync } from 'fflate'
import {
  FINANCIAL_CARRIERS,
  FINANCIAL_DEUTSCHE_POST_HEADERS,
  FINANCIAL_FILE_MAX_COLUMNS,
  FINANCIAL_FILE_MAX_EXPANDED_BYTES,
  FINANCIAL_FILE_MAX_ROWS,
  FINANCIAL_PREVIEW_ROWS,
} from '@/const/Financial'
import readExcelFile from 'read-excel-file/browser'

const sourceText = (value: CellValue<string> | null): string => {
  if (value === null) return ''
  // Excel dates have no time zone. Keep their calendar value without local-time conversion.
  if (value instanceof Date)
    return value
      .toISOString()
      .replace('T', ' ')
      .replace(/(?:\.000)?Z$/, '')
  return String(value)
}

const expectedHeaders = (carrier: FinancialCarrier): string[][] => {
  switch (carrier) {
    case 'Deutsche Post':
      return [FINANCIAL_DEUTSCHE_POST_HEADERS, ['Level', 'Invoice', 'Tracking number', 'Total revenue', 'Currency']]
    case 'CityMail':
      return [['Datum', 'PaketID', 'Produkttyp', 'Referens', 'Paket pris']]
    case 'PostNord':
      return [['Invoice number', 'Item ID', 'Service name', 'Gross amount', 'Fuel surcharge']]
    case 'Earlybird':
      return [
        ['CustomerId', 'TrackingId', 'Price', 'EventTime'],
        ['FeeTrackingId', 'FeeDefinitionName', 'FeeAmount', 'FeeCreated'],
        ['Product', 'Quantity', 'Total Weight', 'Price', 'Sum'],
      ]
  }
}

// The columns that identify each carrier's original file. The full template is still checked when it is read.
const CARRIER_COLUMNS: Record<FinancialCarrier, string[][]> = {
  CityMail: [['PaketID', 'Paket pris']],
  PostNord: [['Item ID', 'Gross amount']],
  Earlybird: [
    ['TrackingId', 'EventTime'],
    ['FeeTrackingId', 'FeeAmount'],
    ['Product', 'Total Weight', 'Sum'],
  ],
  'Deutsche Post': [['Shipment ID', 'Product Code', 'Net Price', 'Weight Unit'], ['Level', 'Tracking number', 'Total revenue']],
}

// The carriers whose columns appear in one of these rows (a CSV header, or the first rows of a worksheet).
export const financialCarriersIn = (rows: string[][]): FinancialCarrier[] =>
  FINANCIAL_CARRIERS.filter((carrier) =>
    rows.some((values) => CARRIER_COLUMNS[carrier].some((required) => required.every((header) => values.includes(header)))),
  )

// Each carrier's original file has its own columns, so the file itself says which carrier invoiced it.
export const financialCarrierOf = (tables: string[][][]): FinancialCarrier => {
  const found = tables.map(financialCarriersIn)
  if (found.some((carriers) => carriers.length !== 1))
    throw new Error('The columns do not match a CityMail, Earlybird, PostNord or Deutsche Post invoice. Upload the original carrier file.')
  const carriers = [...new Set(found.map(([carrier]) => carrier))]
  if (carriers.length > 1)
    throw new Error('The worksheets come from different carriers. Upload one carrier invoice per file.')
  return carriers[0]
}

const worksheetNotice = (carrier: FinancialCarrier, headers: string[]): string => {
  switch (carrier) {
    case 'Deutsche Post':
      return headers.includes('Level')
        ? 'Level 2 contains summaries; Level 3 contains shipment details. Do not add both levels together. This report cannot be imported.'
        : 'Net Price is the cost of each row in Currency. Keep delivery and surcharge rows for the same Shipment ID. Weight is in KGM; blank surcharge weights are not zero.'
    case 'CityMail':
      return 'A PaketID can appear on a base charge and a later surcharge. Keep both rows. This file does not specify a currency or invoice number.'
    case 'PostNord':
      return 'Some rows cover several shipments and have no Item ID. Keep surcharge columns and repeated Item IDs. This file does not specify a currency.'
    case 'Earlybird':
      if (headers.includes('FeeTrackingId'))
        return 'These are separate surcharge rows. Match FeeTrackingId to the shipment without replacing its base charge. Currency is not specified.'
      if (headers.includes('Product'))
        return 'This sheet contains control totals, including repeated grand totals. Do not import these as additional charge rows.'
      return 'TrackingId identifies the shipment. Inspect the surcharge and total sheets as well. Currency and invoice number are not specified.'
  }
}

export const previewFinancialWorksheets = (
  sheets: Sheet<string>[],
  given?: FinancialCarrier,
  allRows = false,
): FinancialFilePreview => {
  if (!sheets.length || sheets.length > 20) throw new Error('Choose a workbook with 1 to 20 worksheets.')
  const carrier = given ?? financialCarrierOf(sheets.map((sheet) => sheet.data.slice(0, 20).map((row) => row.map(sourceText))))
  let totalRows = 0
  const worksheets: FinancialWorksheetPreview[] = sheets.map((sheet) => {
    const headerIndex = sheet.data.slice(0, 20).findIndex((row) => {
      const values = row.map(sourceText)
      return expectedHeaders(carrier).some((required) => required.every((header) => values.includes(header)))
    })
    if (headerIndex < 0)
      throw new Error(
        `Worksheet "${sheet.sheet}" looks like a ${carrier} file but does not match its template. Upload the original, unedited carrier file.`,
      )
    const headers = sheet.data[headerIndex].map(sourceText)
    // Sparse trailing cells are not columns, but empty columns within the table are ambiguous.
    while (headers.at(-1) === '') headers.pop()
    if (headers.length > FINANCIAL_FILE_MAX_COLUMNS) throw new Error('A worksheet can contain at most 100 columns.')
    if (headers.some((header) => !header.trim()) || new Set(headers).size !== headers.length)
      throw new Error(`Worksheet "${sheet.sheet}" needs unique, non-empty column headers.`)
    const rows: string[][] = []
    const rowNumbers: number[] = []
    let rowCount = 0
    for (let index = headerIndex + 1; index < sheet.data.length; index += 1) {
      const sourceRow = sheet.data[index].map(sourceText)
      if (sourceRow.every((cell) => cell === '')) continue
      if (sourceRow.slice(headers.length).some((cell) => cell !== ''))
        throw new Error(`Worksheet "${sheet.sheet}", row ${index + 1} has values outside the header. Check the file.`)
      rowCount += 1
      totalRows += 1
      if (totalRows > FINANCIAL_FILE_MAX_ROWS)
        throw new Error('A workbook can contain at most 100,000 source rows. Contact the PacSort team; do not edit the original.')
      if (allRows || rows.length < FINANCIAL_PREVIEW_ROWS) {
        rows.push(Array.from({ length: headers.length }, (_, column) => sourceRow[column] ?? ''))
        rowNumbers.push(index + 1)
      }
    }
    return {
      name: sheet.sheet,
      headerRow: headerIndex + 1,
      headers,
      rows,
      rowNumbers,
      rowCount,
      notice: worksheetNotice(carrier, headers),
    }
  })
  if (!totalRows) throw new Error('The workbook has headers but no source rows.')
  return { ...worksheets[0], delimiter: null, worksheets, carrier }
}

export const readFinancialWorkbook = async (
  buffer: ArrayBuffer,
  carrier?: FinancialCarrier,
  allRows = false,
): Promise<FinancialFilePreview> => {
  let expandedBytes = 0
  let entries = 0
  try {
    // Inspect the archive directory without inflating any entry before invoking the XML reader.
    unzipSync(new Uint8Array(buffer), {
      filter: (entry): boolean => {
        expandedBytes += entry.originalSize
        entries += 1
        if (expandedBytes > FINANCIAL_FILE_MAX_EXPANDED_BYTES || entries > 1000)
          throw new Error('Workbook is too large when expanded. Contact the PacSort team; do not edit the original.')
        return false
      },
    })
  } catch (error) {
    if (error instanceof Error && error.message.startsWith('Workbook is too large')) throw error
    throw new Error('The XLSX file could not be opened. Export an unprotected XLSX file and try again.', {
      cause: error,
    })
  }
  let sheets: Sheet<string>[]
  try {
    // Preserve decimal precision and numeric identifiers; never evaluate formulas or fetch linked files.
    sheets = await readExcelFile<string>(buffer, { trim: false, parseNumber: (value: string): string => value })
  } catch {
    throw new Error('The XLSX file could not be read. Export an unprotected XLSX file and try again.')
  }
  return previewFinancialWorksheets(sheets, carrier, allRows)
}
